<template lang="pug">
  .app-card.overflow-x-auto
    // Header Section
    .container.mb-4
      .row.align-items-center.py-3.border-bottom.border-primary
        .col-auto
          i.fa.fa-file-invoice.fa-2x.text-primary
        .col
          h4.mb-0.fw-bold.text-primary Invoice Specific Services
          p.text-muted.mb-0.small Specialized services available only for invoices
        .col-auto.d-flex.align-items-center.gap-2
          .badge.bg-info {{ selectedServices.length }} selected
          button.btn.btn-outline-secondary.btn-sm(
            v-if="hasSelectedServices"
            @click="clearAllServices"
            :disabled="state.sending"
          )
            i.fa.fa-times.me-1
            | Clear All

    .container
      .row.g-4
        // Left Column - Service Selection  
        .col-lg-6
          .card.h-100.shadow-sm
            .card-header.bg-light
              .d-flex.align-items-center
                i.fa.fa-list-check.me-2.text-primary
                h5.mb-0.fw-bold Available Invoice Services
            .card-body
              // Invoice Services Grid
              .row.g-2
                .col-md-6(v-for="service in invoiceServices" :key="service.id")
                  .service-card(:class="{ 'selected': service.selected }")
                    .form-check.h-100.p-3
                      input.form-check-input.me-2(
                        type="checkbox"
                        :id="'invoice-service-'+service.id" 
                        v-model="service.selected"
                        @change="onServiceSelect(service)"
                      )
                      label.form-check-label.d-flex.flex-column.flex-grow-1(:for="'invoice-service-'+service.id")
                        .d-flex.align-items-center.mb-2
                          i(:class="['fa', getServiceIcon(service.name), 'me-2', service.selected ? 'text-primary' : 'text-muted']")
                          span.fw-semibold.small {{ service.name }}
                        .mt-auto(v-if="service.selected")
                          .badge.bg-success.small
                            i.fa.fa-check.me-1
                            | Selected

        // Right Column - Price Configuration
        .col-lg-6(v-if="hasSelectedServices")
          .card.h-100.shadow-sm
            .card-header.bg-primary.text-white
              .d-flex.align-items-center
                i.fa.fa-dollar-sign.me-2
                h5.mb-0.fw-bold Price Configuration
                .ms-auto
                  .badge.bg-light.text-primary {{ formatCurrency(totalAmount) }}
            .card-body
              // Selected Services List
              .selected-services-list
                .service-price-item.p-3.border.rounded.mb-2(
                  v-for="service in selectedServices" 
                  :key="service.id"
                  :class="{ 'has-error': service.hasError }"
                )
                  .d-flex.align-items-center.mb-2
                    i(:class="['fa', getServiceIcon(service.name), 'me-2', 'text-primary']")
                    span.fw-semibold {{ service.name }}
                  
                  // Price input
                  .price-section
                    label.form-label.small.text-muted Service Price
                    .input-group
                      span.input-group-text $
                      input.form-control(
                        :class="{ 'is-invalid': service.hasError }"
                        type="number"
                        step="0.01"
                        min="0"
                        :placeholder="service.name === 'Compliance Engine Submission Fee' ? 'Enter price' : 'Enter price'"
                        v-model="service.price"
                        @input="onPriceInput(service)"
                      )
                    .invalid-feedback.small(v-if="service.hasError")
                      i.fa.fa-exclamation-triangle.me-1
                      | This service requires a valid price greater than $0

    // Summary Section
    .container.mt-4(v-if="selectedServices.length > 0")
      .card.shadow
        .card-body
          // Summary Table
          .table-responsive
            table.table.table-hover.mb-3
              thead.table-dark
                tr
                  th.fw-bold.text-dark Service Description
                  th.text-end.fw-bold.text-dark Price
              tbody
                tr(v-for="service in selectedServices" :key="service.id")
                  td
                    .d-flex.align-items-center
                      i(:class="['fa', getServiceIcon(service.name), 'me-2']")
                      | {{ service.name }}
                  td.text-end.fw-semibold {{ formatCurrency(service.price) }}
              tfoot.table-dark
                tr
                  td.text-end.fw-bold.fs-5 TOTAL
                  td.text-end.fw-bold.fs-5.text-success {{ formatCurrency(totalAmount) }}
          
          // Clear All and Save Actions
          .row.align-items-center.mt-3
            .col-md-6(v-if="hasSelectedServices")
              button.btn.btn-outline-danger.btn-lg(@click="clearAllServices" :disabled="state.sending")
                i.fa.fa-trash.me-2
                | Clear All Services
            .col-md-6.text-end(v-if="hasChanges")
              div(v-if="state.sending")
                .spinner-border.spinner-border-sm.me-2(role="status")
                span.text-muted Saving...
              div(v-else)  
                button.btn.btn-success.btn-lg.px-4(@click="saveServices")
                  i.fa.fa-save.me-2
                  | Save Services

    // Save Actions when no services selected but there are changes
    .container.mt-4(v-if="hasChanges && selectedServices.length === 0")
      .card.shadow
        .card-body
          .row.align-items-center
            .col-md-8
              .d-flex.align-items-center
                i.fa.fa-info-circle.me-2.text-info
                span.text-muted All services have been cleared. Save to confirm changes.
            .col-md-4.text-end
              div(v-if="state.sending")
                .spinner-border.spinner-border-sm.me-2(role="status")
                span.text-muted Saving...
              div(v-else)  
                button.btn.btn-success.btn-lg.px-4(@click="saveServices")
                  i.fa.fa-save.me-2
                  | Save Changes
    
    // Error Alert
    .alert.alert-danger.mt-3.d-flex.justify-content-between.align-items-center(v-if="errorMessage")
      .d-flex.align-items-center
        i.fa.fa-exclamation-triangle.me-2
        span {{ errorMessage }}
      button.btn-close(@click="errorMessage = null")
</template>

<script>
export default {
  props: {
    dataInvoice: String,
  },
  
  data() {
    return {
      invoiceServices: [
        { id: 'inspection', name: 'Inspection', selected: false, price: 0, hasError: false },
        { id: 'emergency', name: 'Emergency service', selected: false, price: 0, hasError: false },
        { id: 'compliance', name: 'Compliance Engine Submission Fee', selected: false, price: 0, hasError: false },
        { id: 'dead-run', name: 'Dead Run', selected: false, price: 0, hasError: false }
      ],
      originalData: {},
      totalAmount: 0,
      errorMessage: null,
      state: {
        sending: false
      }
    }
  },

  computed: {
    invoice() {
      return JSON.parse(this.dataInvoice)
    },

    selectedServices() {
      return this.invoiceServices.filter(service => service.selected)
    },

    hasSelectedServices() {
      return this.selectedServices.length > 0
    },

    hasChanges() {
      return JSON.stringify(this.invoiceServices) !== JSON.stringify(this.originalData)
    }
  },

  mounted() {
    this.loadExistingServices()
  },

  methods: {
    loadExistingServices() {
      console.log('Loading existing services...')
      console.log('Invoice data:', this.invoice)
      
      // Primera prioridad: cargar desde marmic_services (nuestro nuevo sistema)
      if (this.invoice.marmic_services) {
        console.log('Found marmic_services:', this.invoice.marmic_services)
        let existingServices = []
        try {
          // Si es string, parsearlo; si ya es array, usarlo directamente
          existingServices = typeof this.invoice.marmic_services === 'string' 
            ? JSON.parse(this.invoice.marmic_services) 
            : this.invoice.marmic_services
        } catch (e) {
          console.error('Error parsing marmic_services:', e)
          existingServices = []
        }

        console.log('Parsed existing services:', existingServices)

        if (Array.isArray(existingServices) && existingServices.length > 0) {
          existingServices.forEach(savedService => {
            const service = this.invoiceServices.find(s => s.name === savedService.name)
            if (service) {
              const price = parseFloat(savedService.price || 0)
              // Solo seleccionar si el precio es mayor a 0
              service.selected = price > 0
              service.price = price
              console.log(`Loaded service: ${service.name}, selected: ${service.selected}, price: ${service.price}`)
            }
          })
          
          // Store original state for change detection
          this.originalData = JSON.parse(JSON.stringify(this.invoiceServices))
          this.calculateTotal()
          console.log('Services loaded from marmic_services successfully')
          return // Salir aquí si encontramos servicios en marmic_services
        }
      }

      console.log('No services found in marmic_services, checking legacy sources...')

      // Fallback: buscar en métodos legacy si no hay nada en marmic_services
      // Load from marmic_invoice_items (InvoiceItem records)
      if (this.invoice.marmic_invoice_items && this.invoice.marmic_invoice_items.length > 0) {
        console.log('Loading from marmic_invoice_items...')
        this.invoice.marmic_invoice_items.forEach(item => {
          const service = this.invoiceServices.find(s => s.name === item.name)
          if (service) {
            const price = parseFloat(item.price || 0)
            // Solo seleccionar si el precio es mayor a 0
            service.selected = price > 0
            service.price = price
          }
        })
      }

      // Also check for Dead Run in additional_services_items
      if (this.invoice.additional_services_items && this.invoice.additional_services_items.length > 0) {
        const deadRunItem = this.invoice.additional_services_items.find(item => 
          item.concept && item.concept.toLowerCase().includes('dead run')
        )
        if (deadRunItem) {
          const deadRun = this.invoiceServices.find(s => s.name === 'Dead Run')
          if (deadRun) {
            const price = parseFloat(deadRunItem.price || 0)
            // Solo seleccionar si el precio es mayor a 0
            deadRun.selected = price > 0
            deadRun.price = price
          }
        }
      }

      // Store original state for change detection
      this.originalData = JSON.parse(JSON.stringify(this.invoiceServices))
      this.calculateTotal()
    },

    onServiceSelect(service) {
      if (!service.selected) {
        // Si se desmarca el servicio, poner precio en 0
        service.price = 0
        service.hasError = false // Limpiar error al desmarcar
      } else {
        // Si se marca el servicio y tiene precio 0 o vacío, asignar precio por defecto
        if (parseFloat(service.price || 0) === 0) {
          // Precios por defecto actualizados según especificaciones
          const defaultPrices = {
            'Inspection': 100.00,
            'Emergency service': 400.00,
            'Compliance Engine Submission Fee': 0, // Sin precio predefinido
            'Dead Run': 250.00
          }
          
          // Solo asignar precio si no es "Compliance Engine Submission Fee"
          if (service.name !== 'Compliance Engine Submission Fee') {
            service.price = defaultPrices[service.name] || 100.00
          } else {
            service.price = 0 // Mantener vacío para Compliance Engine
          }
        }
        service.hasError = false // Limpiar error al seleccionar
      }
      this.calculateTotal()
    },

    calculateTotal() {
      this.totalAmount = this.selectedServices.reduce((sum, service) => {
        return sum + parseFloat(service.price || 0)
      }, 0)
    },

    clearAllServices() {
      // Deseleccionar todos los servicios y resetear precios
      this.invoiceServices.forEach(service => {
        service.selected = false
        service.price = 0
        service.hasError = false
      })
      this.calculateTotal()
    },

    onPriceInput(service) {
      // Limpiar error cuando el usuario empiece a escribir
      service.hasError = false
      this.calculateTotal()
    },

    async saveServices() {
      if (this.state.sending) return

      this.state.sending = true
      this.errorMessage = null
      
      // Limpiar errores previos
      this.invoiceServices.forEach(service => {
        service.hasError = false
      })

      try {
        // Validar que todos los servicios seleccionados tengan precio válido (mayor a 0)
        const invalidServices = this.selectedServices.filter(service => {
          return parseFloat(service.price || 0) <= 0
        })

        if (invalidServices.length > 0) {
          // Marcar servicios con error
          invalidServices.forEach(service => {
            service.hasError = true
          })
          
          const serviceNames = invalidServices.map(s => s.name).join(', ')
          throw new Error(`The following selected services require a valid price greater than $0: ${serviceNames}`)
        }

        // Solo enviar servicios seleccionados que tengan precio mayor a 0
        const selectedServicesData = this.selectedServices
          .filter(service => parseFloat(service.price || 0) > 0)
          .map(service => ({
            name: service.name,
            price: parseFloat(service.price || 0)
          }))

        const response = await fetch(`/iumserv`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            invoice_id: this.invoice.id,
            invoice: {
              marmic_services: selectedServicesData
            }
          })
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Failed to save services')
        }

        // Update original data to reflect saved state
        this.originalData = JSON.parse(JSON.stringify(this.invoiceServices))
        
        // Optional: Show success message or emit event
        this.$emit('services-updated', this.selectedServices)

        // Reload page to show updated services
        setTimeout(() => {
          window.location.reload()
        }, 1000)

      } catch (error) {
        console.error('Error saving services:', error)
        this.errorMessage = error.message || 'An error occurred while saving services'
      } finally {
        this.state.sending = false
      }
    },

    formatCurrency(amount) {
      if (!amount && amount !== 0) return '$0.00'
      return `$${parseFloat(amount).toFixed(2)}`
    },

    getServiceIcon(serviceName) {
      const iconMap = {
        'Inspection': 'fa-search',
        'Emergency service': 'fa-ambulance', 
        'Compliance Engine Submission Fee': 'fa-file-text',
        'Dead Run': 'fa-clock-o'
      }
      return iconMap[serviceName] || 'fa-cog'
    }
  }
}
</script>

<style scoped>
.service-card {
  transition: all 0.3s ease;
  border-radius: 8px;
  overflow: hidden;
}

.service-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.service-card.selected {
  background-color: #f8f9fa;
  border: 2px solid #0d6efd;
}

.service-card.selected .form-check {
  background-color: #e7f3ff;
}

.price-section .input-group {
  border-radius: 6px;
  overflow: hidden;
}

.price-section .form-control:focus {
  border-color: #0d6efd;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
}

.card {
  transition: all 0.3s ease;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.table-dark th,
.table-dark td {
  border-color: rgba(255, 255, 255, 0.1);
}

.btn-success:hover {
  background-color: #198754;
  border-color: #198754;
}

.alert-danger {
  border-radius: 8px;
}

.border-primary {
  border-color: #0d6efd !important;
}

.text-primary {
  color: #0d6efd !important;
}

.bg-light {
  background-color: #f8f9fa !important;
}

/* Estilos para validación de errores */
.is-invalid {
  border-color: #dc3545;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}

.invalid-feedback {
  display: block;
  color: #dc3545;
  font-size: 0.875em;
  margin-top: 0.25rem;
}

.service-price-item.has-error {
  border-color: #dc3545 !important;
  background-color: rgba(220, 53, 69, 0.05);
}
</style>
